import axios, {AxiosInstance} from 'axios';
import Config from '../common/config';
import {message} from "antd";
import jwt_decode from 'jwt-decode';
import axiosService from "./axios.service";

interface TokenSet {
    accessToken: string;
    refreshToken: string;
}

export interface UserObject {
    userName: string;
    exp: number;
    userType: string;
    projects: [];
    isLogin?: boolean;
    userProfile: object;
}

export default class AuthService {

    public static authAxios(attachAuth?: boolean) {
        const http: AxiosInstance = axios.create({
            baseURL: Config.backendAPI,
        });
        http.interceptors.response.use(response => {
            return response;
        }, error => {
            if (error.response.status) {
                switch (error.response.status) {
                    case 401:
                        message.error(
                            'Your login details does not match any records. Please try again.'
                        )
                        return error.response;
                    default:
                        return error.response;
                }
            }
        });
        if (attachAuth) {
            http.interceptors.request.use(request => {
                const token = this.getTokens();
                if (typeof token !== "boolean") {
                    request.headers.Authorization = `Bearer ${token.accessToken}`;
                }
                return request;
            })
        }
        return http;
    }

    public static login(username: string, password: string) {
        return this.authAxios().post(
            'users/token/',
            {
                username: username,
                password: password
            }
        ).then(response => response);
    }

    public static logOut() {
        const tokens = AuthService.getTokens();
        let refreshToken: any = false;
        if (typeof tokens !== "boolean") {
            refreshToken = tokens.refreshToken;
        }
        localStorage.clear();
        return AuthService.authAxios(true).post(
            'users/token/sign-out/',
            {
                refreshToken
            }
        ).then(
            response => {
                return response;
            }
        );
    }

    public static getUserId() {
        const token = this.getLocalToken('access');
        if (token) {
            const payload: any = jwt_decode(token as string);
            return payload['user_id'];
        } else {
            return false;
        }
    }

    public static getUserOrganization() {
        const token = this.getLocalToken('access');
        if (token) {
            const payload: any = jwt_decode(token as string);
            return payload['organization'];
        } else {
            return false;
        }
    }

    private static checkTokenExpiry(token: string) {
        const token_expiry: any = jwt_decode(token);
        return token_expiry['exp'] > (Date.now()/1000);
    }

    public static saveTokens(access?: string, refresh?: string) {
        if (access) {
            localStorage.setItem(Config.accessTokenKey, access);
        }
        if (refresh) {
            localStorage.setItem(Config.refreshTokenKey, refresh);
        }
    }

    public static getLocalToken(tokenType: 'access'| 'refresh') {
        if (tokenType === 'access') {
            const accessToken = localStorage.getItem(Config.accessTokenKey);
            if (!accessToken || !this.checkTokenExpiry(accessToken)) {
                return false;
            }
            return accessToken;
        }
        if (tokenType === 'refresh') {
            const refreshToken = localStorage.getItem(Config.refreshTokenKey);
            if (!refreshToken || !this.checkTokenExpiry(refreshToken)) {
                return false;
            }
            return refreshToken;
        }
    }

    public static getTokens(): TokenSet | boolean {
        const accessToken = localStorage.getItem(Config.accessTokenKey);
        const refreshToken = localStorage.getItem(Config.refreshTokenKey);
        if (!accessToken || !this.checkTokenExpiry(accessToken)) {
            return false;
        }
        if (!refreshToken || !this.checkTokenExpiry(refreshToken)) {
            return false;
        }
        return {
            accessToken,
            refreshToken
        };
    }

    public static checkAccessToken() {
        const tokens: TokenSet | boolean = this.getTokens();
        if (typeof tokens === "boolean") {
            return false;
        }
        return this.authAxios(true).get(
            '/sys/auth-ping/'
        ).then(response => response.status === 200);
    }

    public static refreshToken(refreshToken: string) {
        return this.authAxios().post(
            'users/token/refresh/',
            {
                refresh: refreshToken
            }
        );
    }

    public static getUserProfile() {
        return axiosService({attachAuth: true}).get(
            '/users/profile/'
        );
    }

    public static postUserProfile(data:any) {
        return axiosService({attachAuth: true, useFormData: true}).post(
            '/users/profile/', data
        );
    }

    public static getUserType(): 'internal' | 'client' | undefined {
        const accessToken = this.getLocalToken("access");
        if (accessToken) {
            const tokenContent: any = jwt_decode(accessToken);
            if (tokenContent && tokenContent['user_type'] === 'internal') {
                return 'internal';
            } else if (tokenContent && tokenContent['user_type'] === 'client') {
                return 'client';
            }
        }
        return ;
    }

    public static generateUserObject(tokenPayload: any, profileData: any): UserObject {
        return {
            userName: tokenPayload['user_name'],
            exp: tokenPayload['exp'],
            userType: tokenPayload['user_type'],
            projects: tokenPayload['projects'],
            userProfile: profileData
        }
    }

    public static getCurrentUser(access: any, successCallback?: any, failedCallback?: any) {
        if (access) {
            const tokenPayload: any = jwt_decode(access);
            AuthService.getUserProfile().then(
                (response) => {
                    if (response.status === 200) {
                        if (successCallback) {
                            successCallback(tokenPayload, response.data);
                        }
                    } else {
                        if (failedCallback) {
                            failedCallback();
                        }
                    }
                }
            );
        }
    }

    public static redirectLogin(history: any, currentAddress?: string) {
        history.push({
            pathname: '/login',
            state: {
                prev: currentAddress
            }
        });
    }

    public static sendResetPasswordEmail(email: string) {
        return this.authAxios().post(
            'users/reset-password',
            {
                email: email
            }
        ).then(
            response => {
                return response.status;
            }
        );
    }

    public static verifyPasswordResetToken(token: string) {
        const params = new URLSearchParams({
            token: token
        })
        return this.authAxios().get(
            `users/reset-password/token-validation?${params}`
        ).then(
            response => {
                return response.status;
            }
        );
    }

    public static resetPassword(password: string, token: any) {
        const params = new URLSearchParams({
            token: token
        })
        return this.authAxios().post(
            `/users/reset-password/submit?${params}`,
            JSON.stringify({
                password: password
            }),
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        ).then(
            response => {
                return response.status;
            }
        );
    }

    public static changePassword(old_password: string, new_password: string, confirm_password: string) {
        // const accessToken = this.getLocalToken("access");
        // const params = new URLSearchParams({
        //     token: `${accessToken}`
        // })
        return this.authAxios(true).post(
            `users/change-password/`,
            JSON.stringify({
                old_password: old_password,
                new_password: new_password,
                confirm_password: confirm_password,
            }),
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        ).then(
            response => {
                return response.status;
            }
        );
    }
}
